import React, { useState, useEffect, useCallback } from 'react'
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import {
  getAboutOrganization,
  addAboutOrganization,
  updateAboutOrganization,
  updateAboutOrganizationStatus,
  deleteAboutOrganization
} from '../../../services/adminService';
import Loader from '../../../components/common/Loader';
import NoData from '../../../components/common/NoData';
import { usePermissions } from '../../../context/PermissionsContext';

function AboutOrganization() {
  const { permissions } = usePermissions();
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [organizationData, setOrganizationData] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    recordsPerPage: 10
  });
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });
  const [formErrors, setFormErrors] = useState({
    name: '',
    description: ''
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteOrgData, setDeleteOrgData] = useState(null);
  const [statusLoading, setStatusLoading] = useState(null);

  // Constants for validation
  const MAX_NAME_LENGTH = 100;
  const MAX_DESCRIPTION_LENGTH = 500;
  const MIN_NAME_LENGTH = 3;
  const MIN_DESCRIPTION_LENGTH = 10;

  // Validation function
  const validateForm = () => {
    let isValid = true;
    const errors = {
      name: '',
      description: ''
    };

    // Name validation
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
      isValid = false;
    } else if (formData.name.trim().length < MIN_NAME_LENGTH) {
      errors.name = `Name must be at least ${MIN_NAME_LENGTH} characters`;
      isValid = false;
    } else if (formData.name.trim().length > MAX_NAME_LENGTH) {
      errors.name = `Name cannot exceed ${MAX_NAME_LENGTH} characters`;
      isValid = false;
    }

    // Description validation
    if (!formData.description.trim()) {
      errors.description = 'Description is required';
      isValid = false;
    } else if (formData.description.trim().length < MIN_DESCRIPTION_LENGTH) {
      errors.description = `Description must be at least ${MIN_DESCRIPTION_LENGTH} characters`;
      isValid = false;
    } else if (formData.description.trim().length > MAX_DESCRIPTION_LENGTH) {
      errors.description = `Description cannot exceed ${MAX_DESCRIPTION_LENGTH} characters`;
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  // Fetch organization data
  const fetchOrganizationData = useCallback(async () => {
    try {
      setTableLoading(true);
      const response = await getAboutOrganization({
        page: pagination.currentPage,
        limit: pagination.recordsPerPage,
        search: search
      });

      // console.log('About Organization Response', response);

      if (response.success) {
        setOrganizationData(response.data.records);
        setPagination(response.data.pagination);
      } else {
        toast.error('Failed to fetch organization data');
      }
    } catch (error) {
      console.error('Error fetching organization data:', error);
      toast.error('Error fetching organization data');
    } finally {
      setTimeout(() => {
        setTableLoading(false);
      }, 500);
    }
  }, [pagination.currentPage, pagination.recordsPerPage, search]);

  // Load data on component mount and when dependencies change
  useEffect(() => {
    fetchOrganizationData();
  }, [fetchOrganizationData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Clear error when user starts typing
    setFormErrors(prev => ({
      ...prev,
      [name]: ''
    }));

    // Prevent typing if max length is reached
    if (
      (name === 'name' && value.length <= MAX_NAME_LENGTH) ||
      (name === 'description' && value.length <= MAX_DESCRIPTION_LENGTH)
    ) {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    // Check permissions based on mode
    if (isEditMode && !permissions.settings_about_organization_edit) {
      toast.warning("You don't have permission to edit organization information");
      return;
    }
    if (!isEditMode && !permissions.settings_about_organization_create) {
      toast.warning("You don't have permission to create organization information");
      return;
    }

    try {
      setLoading(true);
      setTableLoading(true);

      if (isEditMode && editingId) {
        const updateData = {
          id: editingId,
          name: formData.name.trim(),
          description: formData.description.trim()
        };
        
        const response = await updateAboutOrganization(editingId, updateData);
        if (response.success) {
          toast.success('Organization information updated successfully');
          await fetchOrganizationData();
        } else {
          toast.error(response.message || 'Failed to update organization information');
        }
      } else {
        const newOrgData = {
          name: formData.name.trim(),
          description: formData.description.trim()
        };
        const response = await addAboutOrganization(newOrgData);
        if (response.success) {
          toast.success('Organization information added successfully');
          await fetchOrganizationData();
        } else {
          toast.error(response.message || 'Failed to add organization information');
        }
      }

      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(error.response?.data?.message || 'Failed to submit form');
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '' });
    setFormErrors({ name: '', description: '' });
    setIsEditMode(false);
    setEditingId(null);
  };

  const handleEdit = (item) => {
    if (!item || !item.id) {
      toast.error('Invalid item data');
      return;
    }
    setFormData({
      name: item.name,
      description: item.description
    });
    setEditingId(item.id);
    setIsEditMode(true);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!permissions.settings_about_organization_delete) {
      toast.warning("You don't have permission to delete organization information");
      return;
    }

    try {
      setLoading(true);
      setTableLoading(true);
      const response = await deleteAboutOrganization(id);
      if (response.success) {
        toast.success('Organization information deleted successfully');
        await fetchOrganizationData();
      } else {
        toast.error(response.message || 'Failed to delete organization information');
      }
    } catch (error) {
      console.error('Error deleting organization info:', error);
      toast.error(error.response?.data?.message || 'Error deleting organization information');
    } finally {
      setShowDeleteModal(false);
      setDeleteOrgData(null);
      setLoading(false);
      setTableLoading(false);
    }
  };

  const handleStatusToggle = async (id, currentStatus) => {
    if (!permissions.settings_about_organization_status) {
      toast.warning("You don't have permission to change organization status");
      return;
    }

    try {
      setStatusLoading(id);
      setTableLoading(true);
      const response = await updateAboutOrganizationStatus(id, { 
        id: id,
        status: !currentStatus 
      });
      if (response.success) {
        toast.success(`Organization information ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
        await fetchOrganizationData();
      } else {
        toast.error(response.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.response?.data?.message || 'Error updating status');
    } finally {
      setStatusLoading(null);
      setTableLoading(false);
    }
  };

  const handleDeleteClick = (e, id, name) => {
    e.stopPropagation();
    setDeleteOrgData({ id, name });
    setShowDeleteModal(true);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setPagination(prev => ({
      ...prev,
      recordsPerPage: newLimit,
      currentPage: 1
    }));
  };

  const handleAddNew = () => {
    resetForm();
    setShowModal(true);
  };

  const getStatusBadge = (isActive) => {
    if (isActive) {
      return (
        <span className="px-2 py-1 rounded text-sm border border-green-500 bg-green-500/10 text-green-500 inline-flex items-center gap-1">
          <Icon icon="mdi:check-circle-outline" className="text-base" />
          Active
        </span>
      );
    }
    return (
      <span className="px-2 py-1 rounded text-sm border border-red-500 bg-red-500/10 text-red-500 inline-flex items-center gap-1">
        <Icon icon="mdi:close-circle-outline" className="text-base" />
        Inactive
      </span>
    );
  };

  return (
    <>
      {/* Page content – single card with toolbar + table + footer */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Toolbar: search + add button – one row, clear spacing */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-4 sm:px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div className="relative w-full sm:max-w-[320px]">
            <Icon
              icon="fluent:search-20-regular"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none"
            />
            <input
              type="search"
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
              placeholder="Search organization info..."
              value={search}
              onChange={handleSearchChange}
              aria-label="Search organization information"
            />
          </div>
          <button
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-white font-medium text-sm shadow-sm whitespace-nowrap w-full sm:w-auto min-w-[200px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleAddNew}
            disabled={loading || !permissions.settings_about_organization_create}
            title={!permissions.settings_about_organization_create ? "You don't have permission to create organization information" : ""}
          >
            {loading ? (
              <>
                <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Loading...</span>
              </>
            ) : (
              <>
                <Icon icon="fluent:building-24-regular" width={18} height={18} />
                <span>Add Organization Info</span>
              </>
            )}
          </button>
        </div>

      {/* table */}
      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden mb-3">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ minWidth: '800px' }}>
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">SL. No.</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-1/5">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-2/5">Description</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider w-32">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tableLoading ? (
                <tr>
                  <td colSpan="5" className="px-4 py-12 text-center">
                    <Loader
                      caption="Loading Organization Data..."
                      minLoadTime={500}
                      containerHeight="200px"
                    />
                  </td>
                </tr>
              ) : organizationData.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-12 text-center">
                    <NoData caption="No organization information found" />
                  </td>
                </tr>
              ) : (
                organizationData.map((item, index) => (
                  <tr key={item.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3 text-gray-500 font-medium">
                      {(pagination.currentPage - 1) * pagination.recordsPerPage + index + 1}
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">{item.name}</td>
                    <td className="px-4 py-3 text-gray-600">{item.description}</td>
                    <td className="px-4 py-3">
                      {getStatusBadge(item.is_active)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-50 transition-colors"
                          title={!permissions.settings_about_organization_edit ? "You don't have permission to edit" : "Edit Info"}
                          onClick={() => handleEdit(item)}
                          disabled={loading || !permissions.settings_about_organization_edit}
                        >
                          <Icon icon="fluent:edit-24-regular" width="18" height="18" />
                        </button>
                        <button
                          className={`p-2 rounded-lg disabled:opacity-50 transition-colors ${
                            item.is_active
                              ? "text-green-600 hover:text-green-700 hover:bg-green-50"
                              : "text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
                          }`}
                          title={!permissions.settings_about_organization_status ? "You don't have permission to change status" : (item.is_active ? "Deactivate" : "Activate")}
                          onClick={() => handleStatusToggle(item.id, item.is_active)}
                          disabled={statusLoading === item.id || !permissions.settings_about_organization_status}
                        >
                          {statusLoading === item.id ? (
                            <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full inline-block" role="status" aria-hidden="true"></span>
                          ) : (
                            <Icon 
                              icon={item.is_active ? "fluent:checkmark-circle-24-regular" : "fluent:dismiss-circle-24-regular"}
                              width="18" 
                              height="18" 
                            />
                          )}
                        </button>
                        <button
                          className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
                          title={!permissions.settings_about_organization_delete ? "You don't have permission to delete" : "Delete Info"}
                          onClick={(e) => handleDeleteClick(e, item.id, item.name)}
                          disabled={loading || !permissions.settings_about_organization_delete}
                        >
                          <Icon icon="fluent:delete-24-regular" width="18" height="18" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer: rows per page + pagination – grouped inside same card */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-4 sm:px-6 py-4 border-t border-gray-100 bg-gray-50/30">
          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
            <span className="hidden sm:inline">Rows per page</span>
            <select
              className="px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm bg-white"
              value={pagination.recordsPerPage}
              onChange={handleLimitChange}
              aria-label="Rows per page"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="text-gray-500">
              {pagination.totalRecords === 0
                ? 'No records'
                : `Showing ${(pagination.currentPage - 1) * pagination.recordsPerPage + 1}–${Math.min(
                    pagination.currentPage * pagination.recordsPerPage,
                    pagination.totalRecords
                  )} of ${pagination.totalRecords}`}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1 || loading || pagination.totalPages === 0}
              title="Previous page"
              aria-label="Previous page"
            >
              <Icon icon="fluent:chevron-left-24-regular" width={16} height={16} />
            </button>
            <span className="min-w-[100px] text-center text-sm text-gray-700">
              Page <span className="font-semibold">{pagination.totalPages === 0 ? 0 : pagination.currentPage}</span>
              <span className="text-gray-500"> of </span>
              <span className="font-semibold">{pagination.totalPages || 0}</span>
            </span>
            <button
              type="button"
              className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages || loading || pagination.totalPages === 0}
              title="Next page"
              aria-label="Next page"
            >
              <Icon icon="fluent:chevron-right-24-regular" width={16} height={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" tabIndex="-1" role="dialog" aria-modal="true">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <h2 className="text-lg font-semibold text-gray-900">
                {isEditMode ? 'Edit organization' : 'Add organization'}
              </h2>
              <button
                type="button"
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                onClick={() => { setShowModal(false); resetForm(); }}
                aria-label="Close"
              >
                <Icon icon="mdi:close" width="24" height="24" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="flex flex-col">
              <div className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={`w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${formErrors.name ? 'border-red-500' : 'border-gray-200'}`}
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter name"
                    disabled={loading}
                  />
                  <div className="mt-1.5 flex items-center justify-between gap-2">
                    {formErrors.name && <span className="text-red-500 text-sm">{formErrors.name}</span>}
                    <span className="text-gray-400 text-xs ml-auto">{formData.name.length}/{MAX_NAME_LENGTH}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    className={`w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-y min-h-[100px] ${formErrors.description ? 'border-red-500' : 'border-gray-200'}`}
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter description"
                    rows={4}
                    disabled={loading}
                  />
                  <div className="mt-1.5 flex items-center justify-between gap-2">
                    {formErrors.description && <span className="text-red-500 text-sm">{formErrors.description}</span>}
                    <span className="text-gray-400 text-xs ml-auto">{formData.description.length}/{MAX_DESCRIPTION_LENGTH}</span>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-100 flex flex-row-reverse gap-3 bg-gray-50/30">
                <button
                  type="submit"
                  className="px-4 py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-white font-medium text-sm disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : (isEditMode ? 'Update' : 'Save')}
                </button>
                <button
                  type="button"
                  className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-100 font-medium text-sm disabled:opacity-50"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
            <div className="px-6 py-5 text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-red-50 text-red-500 mb-4">
                <Icon icon="fluent:delete-24-regular" width="28" height="28" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete organization?</h3>
              <p className="text-gray-500 text-sm">
                “{deleteOrgData?.name}” will be removed. This cannot be undone.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex flex-row-reverse gap-3 bg-gray-50/30">
              <button
                type="button"
                className="px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white font-medium text-sm flex items-center gap-2 disabled:opacity-50"
                onClick={() => handleDelete(deleteOrgData?.id)}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" role="status" aria-hidden="true" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Icon icon="fluent:delete-24-regular" width="16" height="16" />
                    Delete
                  </>
                )}
              </button>
              <button
                type="button"
                className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-100 font-medium text-sm"
                onClick={() => { setShowDeleteModal(false); setDeleteOrgData(null); }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  )
}

export default AboutOrganization
